// @flow

import moment from 'moment'
import { createActions, handleActions } from 'redux-actions'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import userReservationResource from 'frontend_core/dist/rest/resources/userReservation'
import masterResource from 'frontend_core/dist/rest/resources/master'
import type { Master } from 'types/v1/master'
import type { Reputation } from 'types/v1/reputation'
import { SET_UI_STATE } from './ui'

/**
 ************************************************************
 * 初期ステート
 ************************************************************
 **/

export type State = {
  roomFeatures: Array<Master>,
  hasCreated: boolean,
  successCreateOrUpdateReputation: boolean,
  // 失敗メッセージ
  failedRoomFeatures: ?string,
  failedReputation: ?string,
}

const initialState: State = {
  roomFeatures: [],
  hasCreated: false,
  successCreateOrUpdateReputation: false,
  // 失敗メッセージ
  failedRoomFeatures: null, // TODO: マスタ取得した場合にどうするか考える
  failedReputation: null,
}

/**
 ************************************************************
 * ActionTypes
 ************************************************************
 **/

const actionTypes = {
  FETCH_ROOM_FEATURES: 'FETCH_ROOM_FEATURES',
  SUCCESS_ROOM_FEATURES: 'SUCCESS_ROOM_FEATURES',
  FAILED_ROOM_FEATURES: 'FAILED_ROOM_FEATURES',
  FETCH_REPUTATION: 'FETCH_REPUTATION',
  SUCCESS_FETCH_REPUTATION: 'SUCCESS_FETCH_REPUTATION',
  FAILED_FETCH_REPUTATION: 'FAILED_FETCH_REPUTATION',
  CREATE_OR_UPDATE_REPUTATION: 'CREATE_OR_UPDATE_REPUTATION',
  SUCCESS_CREATE_OR_UPDATE_REPUTATION: 'SUCCESS_CREATE_OR_UPDATE_REPUTATION',
  FAILED_CREATE_OR_UPDATE_REPUTATION: 'FAILED_CREATE_OR_UPDATE_REPUTATION',
}

/**
 ************************************************************
 * Actions
 ************************************************************
 **/

export const actions = createActions(
  actionTypes.FETCH_ROOM_FEATURES,
  actionTypes.SUCCESS_ROOM_FEATURES,
  actionTypes.FAILED_ROOM_FEATURES,
  actionTypes.FETCH_REPUTATION,
  actionTypes.SUCCESS_FETCH_REPUTATION,
  actionTypes.FAILED_FETCH_REPUTATION,
  actionTypes.CREATE_OR_UPDATE_REPUTATION,
  actionTypes.SUCCESS_CREATE_OR_UPDATE_REPUTATION,
  actionTypes.FAILED_CREATE_OR_UPDATE_REPUTATION,
)

/**
 ************************************************************
 * reducer
 ************************************************************
 **/
export const reducer = handleActions(
  {
    [actions.fetchRoomFeatures]: (state: State) => ({
      ...state,
      failedRoomFeatures: null,
    }),
    [actions.successRoomFeatures]: (state: State, action: Object) => ({
      ...state,
      roomFeatures: action.payload,
    }),
    [actions.failedRoomFeatures]: (state: State, action: Object) => ({
      ...state,
      failedRoomFeatures: action.payload,
    }),
    [actions.fetchReputation]: (state: State) => ({
      ...state,
      hasCreated: false,
      failedReputation: null,
    }),
    [actions.successFetchReputation]: (state: State, action: Object) => ({
      ...state,
      hasCreated: true,
    }),
    [actions.failedFetchReputation]: (state: State, action: Object) => ({
      ...state,
    }),
    [actions.createOrUpdateReputation]: (state: State) => ({
      ...state,
      successCreateOrUpdateReputation: false,
      failedReputation: null,
    }),
    [actions.successCreateOrUpdateReputation]: (
      state: State,
      action: Object,
    ) => ({
      ...state,
      successCreateOrUpdateReputation: true,
      hasCreated: true,
    }),
    [actions.failedCreateOrUpdateReputation]: (
      state: State,
      action: Object,
    ) => ({
      ...state,
      failedReputation: action.payload,
    }),
  },
  initialState,
)

/*
 ************************************************************
 * API連携 非同期アクション
 ************************************************************
 */

function* fetchRoomFeatures() {
  try {
    const roomFeatures = yield call(masterResource.roomFeatures)
    yield put({
      type: actionTypes.SUCCESS_ROOM_FEATURES,
      payload: roomFeatures,
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_ROOM_FEATURES,
      payload: e.message,
    })
  }
}

function getReputationUIState(reputation: Reputation) {
  return {
    score: reputation.score,
    description: reputation.description,
    roomFeatures: (reputation.room_features || []).map(feature => feature.name),
    roomNps: reputation.room_net_promoter_score,
    roomDescription: reputation.room_description,
    nps: reputation.net_promoter_score,
    serviceDescription: reputation.service_description,
  }
}

function* fetchReputation({
  payload,
}: {
  payload: {
    userId: string,
    reservationId: string,
  },
}) {
  const { userId, reservationId } = payload
  try {
    const reputation: Reputation = yield call(
      userReservationResource.reputation,
      userId,
      reservationId,
    )
    yield put({
      type: actionTypes.SUCCESS_FETCH_REPUTATION,
    })
    yield put({
      type: SET_UI_STATE,
      payload: getReputationUIState(reputation),
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_FETCH_REPUTATION,
      payload: e.message,
    })
  }
}

function* createOrUpdateReputation({
  payload,
}: {
  payload: {
    userId: string,
    reservationId: string,
    hasCreated: boolean,
    uiState: {
      score: number,
      description: string,
      roomFeatures: Array<string>,
      roomNps: number,
      roomDescription?: ?string,
      nps: string,
      serviceDescription?: ?string,
    },
  },
}) {
  const { userId, reservationId, hasCreated, uiState } = payload

  yield put({
    type: SET_UI_STATE,
    payload: {
      isLoading: true,
    },
  })

  try {
    const request = hasCreated
      ? userReservationResource.updateReputation
      : userReservationResource.createReputation
    const reputation: Reputation = yield call(request, userId, reservationId, {
      score: uiState.score,
      description: uiState.description,
      room_features: uiState.roomFeatures,
      room_net_promoter_score: uiState.roomNps,
      room_description: uiState.roomDescription,
      net_promoter_score: uiState.nps,
      service_description: uiState.serviceDescription,
    })
    yield put({
      type: actionTypes.SUCCESS_CREATE_OR_UPDATE_REPUTATION,
    })
    yield put({
      type: SET_UI_STATE,
      payload: getReputationUIState(reputation),
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_CREATE_OR_UPDATE_REPUTATION,
      payload: e.message,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        isLoading: false,
      },
    })
  }
}

/**
 ************************************************************
 * saga
 ************************************************************
 **/

export const reservationReputationSagas = [
  takeEvery(actionTypes.FETCH_ROOM_FEATURES, fetchRoomFeatures),
  takeEvery(actionTypes.FETCH_REPUTATION, fetchReputation),
  takeEvery(actionTypes.CREATE_OR_UPDATE_REPUTATION, createOrUpdateReputation),
]



// WEBPACK FOOTER //
// ./src/redux/modules/reservationReputation.js